import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Product, Drug } from '../types';
import { showToast } from '../utils/toast';

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [drugs, setDrugs] = useState<Drug[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Format the products
        const formattedProducts: Product[] = data.map(product => ({
          id: product.id,
          name: product.name,
          category: product.category,
          description: product.description || '',
          price: parseFloat(product.price),
          images: product.images,
          inStock: product.in_stock,
          stockCount: product.stock_count,
          prescriptionRequired: product.prescription_required,
          manufacturer: product.manufacturer || '',
          rating: parseFloat(product.rating),
          reviews: product.reviews_count,
          tags: product.tags
        }));

        setProducts(formattedProducts);
        
        // Also format as drugs for the drug dictionary
        const formattedDrugs: Drug[] = data
          .filter(product => product.category === 'Antibiotics' || 
                            product.category === 'Pain Relief' || 
                            product.category === 'Vitamins' ||
                            product.category.includes('Medicine'))
          .map(product => ({
            id: product.id,
            name: product.name,
            genericName: product.name.split(' ')[0], // Simplification - in real app would be a separate field
            category: product.category,
            description: product.description || '',
            uses: product.tags.map(tag => tag.replace('-', ' ')),
            dosage: 'Refer to product packaging or consult a healthcare professional.',
            sideEffects: ['Refer to product packaging for side effects'],
            contraindications: ['Consult a healthcare professional before use'],
            interactions: [],
            warnings: product.prescription_required ? ['Prescription required'] : [],
            manufacturer: product.manufacturer || '',
            prescriptionRequired: product.prescription_required,
            price: parseFloat(product.price),
            availability: product.in_stock ? 'in-stock' : product.stock_count > 0 ? 'limited' : 'out-of-stock'
          }));

        setDrugs(formattedDrugs);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch products'));
        showToast.system.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return {
    products,
    drugs,
    loading,
    error
  };
}